import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, Loader2, Send } from "lucide-react";
import { api, apiErr } from "@/lib/api";

export default function PublicBooking() {
  const { workspaceId } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", address: "", preferred_at: "", type: "site_survey", notes: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const [bdate, setBdate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  useEffect(() => {
    if (info?.mode !== "slots" || !bdate) { setSlots([]); return; }
    setSlotsLoading(true); setF((s) => ({ ...s, preferred_at: "" }));
    api.get(`/public/booking/${workspaceId}/slots`, { params: { date: bdate } })
      .then(({ data }) => setSlots(data.slots || [])).catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [bdate, info, workspaceId]);

  useEffect(() => {
    api.get(`/public/booking/${workspaceId}`).then(({ data }) => setInfo(data)).catch(() => setNotFound(true));
  }, [workspaceId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.preferred_at) { toast.error("Name and preferred time are required"); return; }
    setBusy(true);
    try {
      await api.post(`/public/booking/${workspaceId}`, { ...f, preferred_at: new Date(f.preferred_at).toISOString() });
      setSent(true);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Could not submit"); }
    setBusy(false);
  };

  const inp = "w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none";
  const lbl = "mb-1 block font-mono-tac text-[10px] uppercase tracking-widest text-slate-500";

  if (notFound) return <div className="grid min-h-screen place-items-center bg-[#0B0F17] text-slate-400" data-testid="booking-notfound">This booking link is not available.</div>;

  return (
    <div className="min-h-screen bg-[#0B0F17] px-4 py-12 text-slate-200" data-testid="public-booking-page">
      <div className="mx-auto max-w-xl">
        <div className="mb-6 flex items-center gap-2.5">
          <Logo />
        </div>
        <div className="rounded-2xl border border-slate-800 bg-[#0d1420] p-6">
          <div className="flex items-center gap-2 text-amber-400"><CalendarCheck className="h-5 w-5" /><span className="eyebrow">Book a visit</span></div>
          <h1 className="mt-2 font-display text-2xl font-bold text-white">{info ? `Request a time with ${info.company}` : "Loading…"}</h1>
          <p className="mt-1 text-sm text-slate-400">Tell us when works and what you need. We'll confirm your appointment shortly.</p>

          {sent ? (
            <div className="mt-6 rounded-lg border border-emerald-600/40 bg-emerald-500/10 px-4 py-6 text-center text-emerald-300" data-testid="booking-success">
              Thanks! Your request is in. We'll reach out to confirm your time.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                <label>Website<input data-testid="booking-website" type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} /></label>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className={lbl}>Name</label><input data-testid="booking-name" value={f.name} onChange={set("name")} placeholder="Your name" className={inp} /></div>
                <div><label className={lbl}>Phone</label><input data-testid="booking-phone" value={f.phone} onChange={set("phone")} placeholder="+1…" className={inp} /></div>
                <div><label className={lbl}>Email</label><input data-testid="booking-email" type="email" value={f.email} onChange={set("email")} placeholder="you@email.com" className={inp} /></div>
                <div><label className={lbl}>Service type</label>
                  <select data-testid="booking-type" value={f.type} onChange={set("type")} className={inp}>
                    {info && Object.entries(info.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2"><label className={lbl}>Property address</label><input data-testid="booking-address" value={f.address} onChange={set("address")} placeholder="Where is the work?" className={inp} /></div>
                <div className="sm:col-span-2">
                  <label className={lbl}>{info?.mode === "slots" ? "Pick a day, then a time" : "Preferred date & time"}</label>
                  {info?.mode === "slots" ? (
                    <>
                      <input data-testid="booking-date" type="date" min={new Date().toISOString().slice(0, 10)} value={bdate} onChange={(e) => setBdate(e.target.value)} className={inp} />
                      {bdate && (
                        <div className="mt-2" data-testid="booking-slots">
                          {slotsLoading ? <div className="text-xs text-slate-500">Loading times…</div>
                            : slots.length === 0 ? <div className="text-xs text-slate-500">No open times that day. Try another date.</div>
                              : <div className="flex flex-wrap gap-2">
                                  {slots.map((s) => (
                                    <button key={s.iso} type="button" data-testid={`slot-${s.iso}`} onClick={() => setF((v) => ({ ...v, preferred_at: s.iso }))}
                                      className={`rounded-full border px-3 py-1.5 text-xs ${f.preferred_at === s.iso ? "border-amber-500 bg-amber-500 text-black" : "border-slate-700 text-slate-200 hover:border-amber-500"}`}>{s.label}</button>
                                  ))}
                                </div>}
                        </div>
                      )}
                    </>
                  ) : (
                    <input data-testid="booking-datetime" type="datetime-local" value={f.preferred_at} onChange={set("preferred_at")} className={inp} />
                  )}
                </div>
              </div>
              <div><label className={lbl}>What do you need?</label><textarea data-testid="booking-notes" rows={3} value={f.notes} onChange={set("notes")} placeholder="Describe the project or issue…" className={inp} /></div>
              <button data-testid="booking-submit" type="submit" disabled={busy} className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {busy ? "Sending…" : "Request appointment"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
